import * as React from 'react';
import Page from '../../components/Page';
import { ButtonStyle } from '../../components/Button';
import { ActionBar, ActionBarStyle } from '../../components/ActionBar';
import { Variant } from '../../product';
import { ErrorFlash, SuccessFlash } from '../../components/Flash';
import { ProfileContext, ProfileManager } from '../../profile';
import { useRequestAsync, getHeaders } from '../../api/client';
import GeneralVariantInfo from './GeneralVariantInfo';
import VariantDimensions from './VariantDimensions';
import VariantAttributesInfo from './VariantAttributesInfo';
import { Link, Redirect } from 'react-router-dom';
import Loader from '../../components/Loader';

type Props = {
  variant: Variant;
};

const JumpLink = (props: any) => (
  <button
    type="button"
    className={`f5 link dim pv3 pa0 db black bn bg-transparent outline-0 tl ${
      props.current === props.href ? 'b' : 'normal'
    }`}
    onClick={() => props.onClick(props.href)}
  >
    {props.children}
  </button>
);

const update = (context: ProfileManager) =>
  useRequestAsync((data: Variant) => ({
    url: data.id
      ? `/store/products/${data.product_id}/variants/${data.id}`
      : `/store/products/${data.product_id}/variants/new`,
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

const deleteVariant = (context: ProfileManager) =>
  useRequestAsync((data: Variant) => ({
    url: `/store/products/${data.product_id}/variants/${data.id}`,
    method: 'DELETE',
    body: JSON.stringify({}),
    headers: getHeaders(context),
  }));

export default function EditProductVariant(props: Props) {
  const context = React.useContext(ProfileContext);
  const [variant, setVariant] = React.useState(props.variant);
  const [section, setSection] = React.useState('general');
  const [state, submit] = update(context);
  const [deleteState, submitDelete] = deleteVariant(context);

  React.useEffect(() => {
    if (state.complete) {
      window.scrollTo(0, 0);
    }
  }, [state]);

  if (state.loading || deleteState.loading) {
    return <Loader />;
  }

  if (deleteState.data && deleteState.complete) {
    return <Redirect to={`/products/${variant.product_id}`} />;
  }

  const handleFormSubmit = () => submit(variant);
  const handlerForDelete = () => submitDelete(variant);

  return (
    <Page title={variant.name || 'A new variant'} subtitle="Edit variant">
      {state.error && <ErrorFlash error={state.error} />}
      {state.complete && state.data && (
        <SuccessFlash text="Variant has been saved" />
      )}
      <div className="flex justify-between">
        <div className="w-20 flex flex-column">
          <Link to="/products" className="f5 link dim pv3 b black">
            ← Back to Products
          </Link>
          <Link
            to={`/products/${variant.product_id}/variants/${variant.id}/stock`}
            className="f5 link dim pv3 b black"
          >
            Manage Stock ({variant.stock_count || 0})
          </Link>
          <hr />
          <JumpLink onClick={setSection} href="general" current={section}>
            General
          </JumpLink>
          <JumpLink onClick={setSection} href="dimensions" current={section}>
            Dimensions
          </JumpLink>
          <JumpLink
            onClick={setSection}
            href="buying_options"
            current={section}
          >
            Buying Options
          </JumpLink>
        </div>
        <div className="w-80 flex-auto">
          <form onSubmit={handleFormSubmit} id="variant">
            {section === 'general' && (
              <GeneralVariantInfo variant={variant} setVariant={setVariant} />
            )}
            {section === 'dimensions' && (
              <VariantDimensions variant={variant} setVariant={setVariant} />
            )}
            {section === 'buying_options' && (
              <VariantAttributesInfo
                variant={variant}
                setVariant={setVariant}
              />
            )}
            <ActionBar
              className={ActionBarStyle.alignCenter}
              actions={[
                {
                  className: ButtonStyle.Confirm,
                  label: 'Save',
                  handler: handleFormSubmit,
                },
                {
                  className: ButtonStyle.Danger,
                  label: 'Delete',
                  handler: handlerForDelete,
                },
              ]}
            />
          </form>
        </div>
      </div>
    </Page>
  );
}
